/**
 * CockpitSettingsPage
 *
 * Edição das configurações do Cockpit (metas, milestones, SLA).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Rocket,
  Save,
  Loader2,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { erpSettingsService } from '../../../services/erpSettingsService'
import { accessGroupService } from '../../../services/erpApi'
import MilestonesEditor from './components/MilestonesEditor'
import AccessGroupPicker from './components/AccessGroupPicker'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'

const EMPTY_FORM = {
  monthly_goal: '',
  milestones: [],
  sla_response_minutes: '',
  avg_ticket_fallback: '',
  recent_deals_limit: '',
  prize_seller_access_group_ids: [],
  prize_manager_access_group_ids: [],
}

function cloneForm(settings) {
  return {
    monthly_goal: String(settings?.monthly_goal ?? ''),
    milestones: (settings?.milestones || []).map((item) => ({
      value: String(item.value ?? ''),
      prize: String(item.prize ?? ''),
      top_seller_prize: String(item.top_seller_prize ?? ''),
    })),
    sla_response_minutes: String(settings?.sla_response_minutes ?? ''),
    avg_ticket_fallback: String(settings?.avg_ticket_fallback ?? ''),
    recent_deals_limit: String(settings?.recent_deals_limit ?? ''),
    prize_seller_access_group_ids: (settings?.prize_seller_access_group_ids || []).map((id) => Number(id)),
    prize_manager_access_group_ids: (settings?.prize_manager_access_group_ids || []).map((id) => Number(id)),
  }
}

function formsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)
}

function validateCockpitForm(form) {
  const errors = []

  const monthlyGoal = Number(form.monthly_goal)
  if (!Number.isFinite(monthlyGoal) || monthlyGoal <= 0) {
    errors.push('Informe uma meta mensal maior que zero.')
  }

  const sla = Number(form.sla_response_minutes)
  if (!Number.isFinite(sla) || sla < 1 || sla > 1440) {
    errors.push('SLA de resposta deve estar entre 1 e 1440 minutos.')
  }

  const avgTicket = Number(form.avg_ticket_fallback)
  if (!Number.isFinite(avgTicket) || avgTicket <= 0) {
    errors.push('Ticket médio fallback deve ser maior que zero.')
  }

  const recentLimit = Number(form.recent_deals_limit)
  if (!Number.isFinite(recentLimit) || !Number.isInteger(recentLimit) || recentLimit < 1 || recentLimit > 50) {
    errors.push('Limite de fechamentos recentes deve ser um inteiro entre 1 e 50.')
  }

  if (!Array.isArray(form.milestones) || form.milestones.length === 0) {
    errors.push('Adicione ao menos um marco de prêmio.')
  } else {
    let previousValue = -Infinity
    form.milestones.forEach((milestone, index) => {
      const value = Number(milestone.value)
      const prize = Number(milestone.prize)
      const topSellerPrize = Number(milestone.top_seller_prize)

      if (!Number.isFinite(value) || value <= 0) {
        errors.push(`Marco ${index + 1}: faturamento inválido.`)
      }
      if (!Number.isFinite(prize) || prize < 0) {
        errors.push(`Marco ${index + 1}: prêmio geral inválido.`)
      }
      if (!Number.isFinite(topSellerPrize) || topSellerPrize < 0) {
        errors.push(`Marco ${index + 1}: prêmio líder inválido.`)
      }
      if (Number.isFinite(value) && value <= previousValue) {
        errors.push('Os marcos devem ter valores estritamente crescentes.')
      }
      if (Number.isFinite(value)) {
        previousValue = value
      }
    })
  }

  if (
    Number.isFinite(monthlyGoal) &&
    form.milestones.length > 0 &&
    Number(form.milestones[form.milestones.length - 1].value) > monthlyGoal
  ) {
    errors.push('O último marco está acima da meta mensal. Revise os valores.')
  }

  const sellerGroupIds = (form.prize_seller_access_group_ids || []).map((id) => Number(id))
  const managerGroupIds = (form.prize_manager_access_group_ids || []).map((id) => Number(id))

  if (sellerGroupIds.some((id) => !Number.isInteger(id) || id <= 0)) {
    errors.push('Selecione apenas grupos de vendedoras válidos.')
  }

  if (managerGroupIds.some((id) => !Number.isInteger(id) || id <= 0)) {
    errors.push('Selecione apenas grupos gerenciais válidos.')
  }

  const overlapping = sellerGroupIds.filter((id) => managerGroupIds.includes(id))
  if (overlapping.length > 0) {
    errors.push('Um grupo não pode estar em vendedoras e gerencial ao mesmo tempo.')
  }

  return errors
}

function buildPayload(form) {
  return {
    monthly_goal: Number(form.monthly_goal),
    milestones: form.milestones.map((item) => ({
      value: Number(item.value),
      prize: Number(item.prize),
      top_seller_prize: Number(item.top_seller_prize || 0),
    })),
    sla_response_minutes: Math.round(Number(form.sla_response_minutes)),
    avg_ticket_fallback: Number(form.avg_ticket_fallback),
    recent_deals_limit: Math.round(Number(form.recent_deals_limit)),
    prize_seller_access_group_ids: (form.prize_seller_access_group_ids || []).map((id) => Number(id)),
    prize_manager_access_group_ids: (form.prize_manager_access_group_ids || []).map((id) => Number(id)),
  }
}

function CockpitSettingsPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const canEdit = hasPermission(MODULES.SETTINGS, ACTIONS.UPDATE)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [defaults, setDefaults] = useState(null)
  const [savedForm, setSavedForm] = useState(EMPTY_FORM)
  const [form, setForm] = useState(EMPTY_FORM)
  const [validationErrors, setValidationErrors] = useState([])
  const [updatedMeta, setUpdatedMeta] = useState(null)
  const [accessGroups, setAccessGroups] = useState([])
  const [loadingGroups, setLoadingGroups] = useState(true)

  const isDirty = useMemo(() => !formsEqual(form, savedForm), [form, savedForm])

  const loadSettings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await erpSettingsService.getNamespace('cockpit')
      if (!response?.success) {
        throw new Error(response?.message || 'Não foi possível carregar as configurações.')
      }

      const settings = response.data?.settings || {}
      const nextForm = cloneForm(settings)
      setDefaults(response.data?.defaults || null)
      setSavedForm(nextForm)
      setForm(nextForm)
      setUpdatedMeta({
        updated_at: response.data?.updated_at,
        updated_by: response.data?.updated_by,
      })
    } catch (err) {
      console.error('Erro ao carregar configurações do Cockpit:', err)
      setError(err.response?.data?.message || err.message || 'Erro ao carregar configurações.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    let cancelled = false

    const loadAccessGroups = async () => {
      setLoadingGroups(true)
      try {
        const response = await accessGroupService.getAll({ active: true })
        if (!cancelled && response?.success) {
          setAccessGroups(response.data || [])
        }
      } catch (err) {
        console.error('Erro ao carregar grupos de acesso:', err)
      } finally {
        if (!cancelled) setLoadingGroups(false)
      }
    }

    loadAccessGroups()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isDirty) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const handleBack = () => {
    if (isDirty && !window.confirm('Existem alterações não salvas. Deseja sair mesmo assim?')) {
      return
    }
    navigate('/erp/configuracoes')
  }

  const handleRestoreDefaults = () => {
    if (!defaults || !canEdit) return
    if (!window.confirm('Restaurar os valores padrão do Cockpit?')) return
    setForm(cloneForm(defaults))
    setValidationErrors([])
  }

  const handleSave = async () => {
    if (!canEdit) return

    const errors = validateCockpitForm(form)
    setValidationErrors(errors)
    if (errors.length > 0) {
      showToast(errors[0], 'warning')
      return
    }

    setSaving(true)
    try {
      const payload = buildPayload(form)
      const response = await erpSettingsService.updateNamespace('cockpit', payload)
      if (!response?.success) {
        throw new Error(response?.message || 'Erro ao salvar configurações.')
      }

      const nextForm = cloneForm(response.data?.settings || payload)
      setSavedForm(nextForm)
      setForm(nextForm)
      setUpdatedMeta({
        updated_at: response.data?.updated_at,
        updated_by: response.data?.updated_by,
      })
      setValidationErrors([])
      showToast('Configurações do Cockpit salvas com sucesso.', 'success')
    } catch (err) {
      const apiDetails = err.response?.data?.details
      const message = apiDetails?.[0] || err.response?.data?.message || err.message || 'Erro ao salvar.'
      setValidationErrors(Array.isArray(apiDetails) ? apiDetails : [message])
      showToast(message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const previewMarkers = useMemo(() => {
    const goal = Number(form.monthly_goal) || 1
    return (form.milestones || [])
      .map((item) => {
        const value = Number(item.value)
        if (!Number.isFinite(value) || value <= 0) return null
        return {
          value,
          left: Math.min((value / goal) * 100, 100),
        }
      })
      .filter(Boolean)
  }, [form.monthly_goal, form.milestones])

  if (loading) {
    return (
      <div className="erp-settings-loading">
        <Loader2 size={28} className="cockpit-settings-spinner" />
        <p>Carregando configurações do Cockpit...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="erp-settings-error">
        <p>{error}</p>
        <button type="button" onClick={loadSettings}>Tentar novamente</button>
      </div>
    )
  }

  return (
    <div className="erp-settings-dashboard cockpit-settings-page">
      <Toast toast={toast} onClose={hideToast} />

      <div className="erp-settings-header">
        <div>
          <button type="button" className="cockpit-settings-back-btn" onClick={handleBack}>
            <ArrowLeft size={16} />
            Voltar ao hub
          </button>
          <h1 className="erp-settings-title">
            Configurações <span className="erp-settings-title-highlight">Cockpit</span>
          </h1>
          <p className="erp-settings-subtitle">
            Metas, prêmios e indicadores da equipe comercial
          </p>
          {updatedMeta?.updated_at && (
            <p className="cockpit-settings-meta">
              Última atualização:{' '}
              {new Date(updatedMeta.updated_at).toLocaleString('pt-BR')}
              {updatedMeta.updated_by?.name ? ` — ${updatedMeta.updated_by.name}` : ''}
            </p>
          )}
        </div>
        <div className="erp-settings-header-actions">
          <div className="cockpit-settings-icon-badge">
            <Rocket size={18} />
          </div>
        </div>
      </div>

      {!canEdit && (
        <div className="cockpit-settings-readonly-banner" role="status">
          Você possui acesso somente leitura. Solicite permissão de atualização em Configurações.
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="erp-settings-error-banner" role="alert">
          <div className="cockpit-settings-validation-list">
            <AlertTriangle size={18} />
            <ul>
              {validationErrors.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="cockpit-settings-grid">
        <section className="cockpit-settings-card">
          <h2 className="cockpit-settings-card-title">Meta geral da empresa</h2>
          <p className="cockpit-settings-card-description">
            Faturamento coletivo alvo da equipe. A barra de progresso do Cockpit usa este valor como referência máxima.
          </p>
          <label className="cockpit-settings-field">
            <span>Meta coletiva (R$)</span>
            <input
              type="number"
              min={1}
              step={1000}
              className="cockpit-settings-input cockpit-settings-input-lg"
              value={form.monthly_goal}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, monthly_goal: e.target.value }))}
            />
          </label>

          <div className="cockpit-settings-preview">
            <div className="cockpit-settings-preview-track">
              <div className="cockpit-settings-preview-fill" style={{ width: '62%' }} />
              {previewMarkers.map((marker) => (
                <span
                  key={marker.value}
                  className="cockpit-settings-preview-marker"
                  style={{ left: `${marker.left}%` }}
                  title={formatCurrency(marker.value)}
                />
              ))}
            </div>
            <p className="cockpit-settings-preview-caption">
              Prévia ilustrativa com marcos sobre a meta de {formatCurrency(form.monthly_goal)}
            </p>
          </div>
        </section>

        <section className="cockpit-settings-card cockpit-settings-card-wide">
          <h2 className="cockpit-settings-card-title">Milestones e prêmios coletivos</h2>
          <p className="cockpit-settings-card-description">
            Quando a empresa atinge cada marco no mês, todas as vendedoras elegíveis recebem o prêmio geral daquele patamar. O prêmio líder é o bônus extra para quem liderar as vendas individuais naquele patamar.
          </p>
          <MilestonesEditor
            milestones={form.milestones}
            disabled={!canEdit}
            onChange={(milestones) => setForm((prev) => ({ ...prev, milestones }))}
          />
        </section>

        <section className="cockpit-settings-card cockpit-settings-card-wide">
          <h2 className="cockpit-settings-card-title">Regras de premiação</h2>
          <p className="cockpit-settings-card-description">
            Grupos participantes da premiação coletiva e do bônus de liderança individual.
          </p>

          <div className="cockpit-settings-group-block">
            <h3 className="cockpit-settings-group-block-title">Grupos de vendedoras</h3>
            <p className="cockpit-settings-group-block-description">
              Usuárias destes grupos participam da premiação coletiva e podem concorrer ao bônus individual.
            </p>
            {!form.prize_seller_access_group_ids?.length && (
              <p className="cockpit-settings-group-legacy-note">
                Nenhum grupo selecionado: o sistema usa o critério legado (flag vendedora ou role/nome &quot;Vendedor&quot;).
              </p>
            )}
            {loadingGroups ? (
              <p className="access-group-picker-empty">Carregando grupos...</p>
            ) : (
              <AccessGroupPicker
                groups={accessGroups.filter(
                  (group) => !form.prize_manager_access_group_ids?.includes(Number(group.id))
                )}
                selectedIds={form.prize_seller_access_group_ids}
                disabled={!canEdit}
                onChange={(ids) => setForm((prev) => ({ ...prev, prize_seller_access_group_ids: ids }))}
              />
            )}
          </div>

          <div className="cockpit-settings-group-block">
            <h3 className="cockpit-settings-group-block-title">Grupos com visão gerencial</h3>
            <p className="cockpit-settings-group-block-description">
              Usuários destes grupos veem o Cockpit da equipe (metas agregadas e prêmios por vendedora).
            </p>
            {!form.prize_manager_access_group_ids?.length && (
              <p className="cockpit-settings-group-legacy-note">
                Nenhum grupo selecionado: o sistema usa o critério legado (role/nome &quot;G.1&quot;).
              </p>
            )}
            {loadingGroups ? (
              <p className="access-group-picker-empty">Carregando grupos...</p>
            ) : (
              <AccessGroupPicker
                groups={accessGroups.filter(
                  (group) => !form.prize_seller_access_group_ids?.includes(Number(group.id))
                )}
                selectedIds={form.prize_manager_access_group_ids}
                disabled={!canEdit}
                onChange={(ids) => setForm((prev) => ({ ...prev, prize_manager_access_group_ids: ids }))}
              />
            )}
          </div>
        </section>

        <section className="cockpit-settings-card">
          <h2 className="cockpit-settings-card-title">Indicadores auxiliares</h2>
          <p className="cockpit-settings-card-description">
            Parâmetros usados nos cards de eficiência e listagem de fechamentos.
          </p>

          <div className="cockpit-settings-fields-grid">
            <label className="cockpit-settings-field">
              <span>SLA de resposta (min)</span>
              <input
                type="number"
                min={1}
                max={1440}
                className="cockpit-settings-input"
                value={form.sla_response_minutes}
                disabled={!canEdit}
                onChange={(e) => setForm((prev) => ({ ...prev, sla_response_minutes: e.target.value }))}
              />
            </label>

            <label className="cockpit-settings-field">
              <span>Ticket médio fallback (R$)</span>
              <input
                type="number"
                min={1}
                className="cockpit-settings-input"
                value={form.avg_ticket_fallback}
                disabled={!canEdit}
                onChange={(e) => setForm((prev) => ({ ...prev, avg_ticket_fallback: e.target.value }))}
              />
            </label>

            <label className="cockpit-settings-field">
              <span>Fechamentos recentes (limite)</span>
              <input
                type="number"
                min={1}
                max={50}
                className="cockpit-settings-input"
                value={form.recent_deals_limit}
                disabled={!canEdit}
                onChange={(e) => setForm((prev) => ({ ...prev, recent_deals_limit: e.target.value }))}
              />
            </label>
          </div>
        </section>
      </div>

      <div className="cockpit-settings-footer">
        <button type="button" className="cockpit-settings-btn-secondary" onClick={handleBack}>
          Voltar
        </button>
        <div className="cockpit-settings-footer-actions">
          <button
            type="button"
            className="cockpit-settings-btn-secondary"
            onClick={handleRestoreDefaults}
            disabled={!canEdit || saving}
          >
            <RotateCcw size={16} />
            Restaurar padrões
          </button>
          <button
            type="button"
            className="cockpit-settings-btn-primary"
            onClick={handleSave}
            disabled={!canEdit || saving || !isDirty}
          >
            {saving ? <Loader2 size={16} className="cockpit-settings-spinner" /> : <Save size={16} />}
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  )
}

export default CockpitSettingsPage
